import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { VecRGBA } from "utils/color";
import { SelectionSet } from "utils/selectionSet";

// Ids are held once per collection, in an immutable SelectionSet (sorted
// Uint32Array, ~4 bytes/id) — see the note in contexts/highlighted/reducer.ts.
// SecondaryHighlight (property search hits) and TransparentSelectionBase can
// approach full-scene size (1M+ ids). `idArr` is the set's sorted backing
// array; it MUST NOT be mutated.

export enum HighlightCollection {
    SecondaryHighlight = "secondaryHighlight",
    FormsNew = "formsNew",
    FormsOngoing = "formsOngoing",
    FormsCompleted = "formsCompleted",
    FormsFinished = "formsFinished",
    ClashObjects1 = "clashObjects1",
    ClashObjects2 = "clashObjects2",
    /**
     * The two objects of a finished object-to-object measurement. Rendered with their default
     * appearance rather than a colour, so they stay readable once the tool x-rays the model around
     * them. The collection's colour is therefore unused.
     */
    ObjectMeasure = "objectMeasure",
    /**
     * Transparent selection base is made of selected objects in transparent mode.
     * Whenever selected object in transparent mode is unselected - it disappears (because default highlight kicks in).
     * To avoid it - we instead add unselected objects into this collection.
     * Selection is not only remembered when we switch to transparent mode, but also when new selection is added e.g. via Model Tree.
     * NOTE: instead of adding to this collection on mode change - we could just add objects one by one when unselecting,
     * but we'll need to rebuild ID array more often (not sure if it's worse actually).
     */
    TransparentSelectionBase = "transparentSelectionBase",
}

export const highlightCollectionKeys = Object.values(HighlightCollection);

function emptyCollection(color: VecRGBA) {
    return {
        ids: SelectionSet.EMPTY,
        idArr: SelectionSet.EMPTY.sortedIds,
        color,
    };
}

export const initialState = {
    [HighlightCollection.SecondaryHighlight]: emptyCollection([1, 1, 0, 1]),
    [HighlightCollection.FormsNew]: emptyCollection([0.5, 0, 0, 1]),
    [HighlightCollection.FormsOngoing]: emptyCollection([1, 0.75, 0, 1]),
    [HighlightCollection.FormsCompleted]: emptyCollection([0, 0, 1, 1]),
    [HighlightCollection.FormsFinished]: emptyCollection([0, 0.5, 0, 1]),
    [HighlightCollection.ClashObjects1]: emptyCollection([1, 0, 0, 1]),
    [HighlightCollection.ClashObjects2]: emptyCollection([0, 0, 1, 1]),
    [HighlightCollection.ObjectMeasure]: emptyCollection([0, 0, 0, 1]),
    [HighlightCollection.TransparentSelectionBase]: emptyCollection([0, 0, 0, 1]),
};

export type State = typeof initialState;
type Key = keyof State;

enum ActionTypes {
    Add,
    Move,
    Remove,
    SetIds,
    SetColor,
    Set,
    SetAll,
    ClearAll,
    ClearForms,
}

function add(collection: Key, ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Add as const,
        collection,
        ids,
    };
}

function move(fromCollection: Key, toCollection: Key, ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Move as const,
        fromCollection,
        toCollection,
        ids,
    };
}

function remove(collection: Key, ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Remove as const,
        collection,
        ids,
    };
}

function setIds(collection: Key, ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.SetIds as const,
        collection,
        ids,
    };
}

function setColor(collection: Key, color: State[Key]["color"]) {
    return {
        type: ActionTypes.SetColor as const,
        collection,
        color,
    };
}

function set(collection: Key, payload: { color: State[Key]["color"]; ids: Iterable<ObjectId> }) {
    return {
        type: ActionTypes.Set as const,
        collection,
        payload,
    };
}

function setAll(payload: State) {
    return {
        type: ActionTypes.SetAll as const,
        payload,
    };
}

function clearAll() {
    return {
        type: ActionTypes.ClearAll as const,
    };
}

function clearForms() {
    return {
        type: ActionTypes.ClearForms as const,
    };
}

export const actions = {
    add,
    move,
    remove,
    setIds,
    setColor,
    set,
    setAll,
    clearAll,
    clearForms,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHighlightCollection = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHighlightCollection>(undefined as any);

function withIds(collection: State[Key], ids: SelectionSet): State[Key] {
    return { ids, idArr: ids.sortedIds, color: collection.color };
}

function cleared(collection: State[Key]): State[Key] {
    return withIds(collection, SelectionSet.EMPTY);
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: withIds(collection, collection.ids.withAdded(action.ids)),
            };
        }
        case ActionTypes.Move: {
            const fromCollection = state[action.fromCollection];
            const toCollection = state[action.toCollection];

            return {
                ...state,
                [action.fromCollection]: withIds(fromCollection, fromCollection.ids.withRemoved(action.ids)),
                [action.toCollection]: withIds(toCollection, toCollection.ids.withAdded(action.ids)),
            };
        }
        case ActionTypes.Remove: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: withIds(collection, collection.ids.withRemoved(action.ids)),
            };
        }
        case ActionTypes.SetIds: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: withIds(collection, SelectionSet.fromIds(action.ids)),
            };
        }
        case ActionTypes.SetColor: {
            const collection = state[action.collection];

            return {
                ...state,
                [action.collection]: {
                    ...collection,
                    color: action.color,
                },
            };
        }
        case ActionTypes.Set: {
            const ids = SelectionSet.fromIds(action.payload.ids);

            return {
                ...state,
                [action.collection]: {
                    ids,
                    idArr: ids.sortedIds,
                    color: action.payload.color,
                },
            };
        }
        case ActionTypes.ClearAll: {
            return {
                ...state,
                [HighlightCollection.SecondaryHighlight]: cleared(state.secondaryHighlight),
                [HighlightCollection.FormsNew]: cleared(state.formsNew),
                [HighlightCollection.FormsOngoing]: cleared(state.formsOngoing),
                [HighlightCollection.FormsCompleted]: cleared(state.formsCompleted),
                [HighlightCollection.FormsFinished]: cleared(state.formsFinished),
                [HighlightCollection.ClashObjects1]: cleared(state[HighlightCollection.ClashObjects1]),
                [HighlightCollection.ClashObjects2]: cleared(state[HighlightCollection.ClashObjects2]),
                [HighlightCollection.TransparentSelectionBase]: cleared(
                    state[HighlightCollection.TransparentSelectionBase],
                ),
            };
        }
        case ActionTypes.SetAll: {
            return action.payload;
        }
        case ActionTypes.ClearForms: {
            return {
                ...state,
                [HighlightCollection.FormsNew]: cleared(state.formsNew),
                [HighlightCollection.FormsOngoing]: cleared(state.formsOngoing),
                [HighlightCollection.FormsCompleted]: cleared(state.formsCompleted),
                [HighlightCollection.FormsFinished]: cleared(state.formsFinished),
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
